/**
 * 2026 workshop interest questionnaire — edit fields here; backend stores arbitrary key/value pairs.
 * Each field `id` becomes a key in interest_responses (no backend deploy needed when changing questions).
 */

export type SurveyFieldType = 'select' | 'radio';

export interface SurveyOption {
  value: string;
  label: string;
}

export interface SurveyField {
  id: string;
  label: string;
  type: SurveyFieldType;
  options: SurveyOption[];
  helper?: string;
  /** Show this field only when another field matches a condition */
  showWhen?: {
    field: string;
    equals?: string;
    in?: string[];
  };
}

export const WORKSHOP_INTEREST_SURVEY: SurveyField[] = [
  {
    id: 'saa_tenure',
    label: 'How long have you been attending SAA meetings?',
    type: 'select',
    options: [
      { value: 'new', label: "I'm new" },
      { value: 'lt_1_year', label: '< 1 year' },
      { value: '1_3_years', label: '1-3 years' },
      { value: '3_10_years', label: '3-10 years' },
      { value: '10_plus_years', label: '10+ years' },
    ],
  },
  {
    id: 'steps_worked',
    label: 'Have you worked the SAA 12 steps before?',
    type: 'radio',
    options: [
      { value: 'none', label: 'No' },
      { value: 'some', label: 'Some steps' },
      { value: 'all', label: 'All 12 steps' },
    ],
  },
  {
    id: 'workshop_experience',
    label: 'Have you worked the steps in a workshop or step-study group before?',
    type: 'radio',
    options: [
      { value: 'no', label: 'No' },
      { value: 'yes', label: 'Yes' },
    ],
    showWhen: { field: 'steps_worked', in: ['some', 'all'] },
  },
  {
    id: 'last_step_completed',
    label: 'Which is the last step you completed?',
    type: 'select',
    options: Array.from({ length: 12 }, (_, i) => ({
      value: String(i + 1),
      label: `Step ${i + 1}`,
    })),
    showWhen: { field: 'steps_worked', equals: 'some' },
  },
  {
    id: 'has_sponsor',
    label: 'Do you currently have a sponsor?',
    type: 'radio',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'no', label: 'No' },
      { value: 'looking', label: 'Looking for one' },
    ],
    helper: 'A sponsor is required to complete Step One.',
  },
  {
    id: 'help_facilitate',
    label: 'Interested in helping facilitate or support the workshop?',
    type: 'radio',
    options: [
      { value: 'yes', label: 'Yes' },
      { value: 'maybe', label: 'Maybe later' },
      { value: 'no', label: 'No' },
    ],
    showWhen: { field: 'steps_worked', equals: 'all' },
  },
];

export type InterestSurveyValues = Record<string, string>;

export function emptySurveyValues(): InterestSurveyValues {
  const values: InterestSurveyValues = {};
  for (const field of WORKSHOP_INTEREST_SURVEY) {
    values[field.id] = '';
  }
  return values;
}

export function isFieldVisible(field: SurveyField, values: InterestSurveyValues): boolean {
  if (!field.showWhen) return true;
  const actual = values[field.showWhen.field] || '';
  if (field.showWhen.equals !== undefined) {
    return actual === field.showWhen.equals;
  }
  if (field.showWhen.in !== undefined) {
    return field.showWhen.in.includes(actual);
  }
  return true;
}

export function visibleSurveyFields(values: InterestSurveyValues): SurveyField[] {
  return WORKSHOP_INTEREST_SURVEY.filter((f) => isFieldVisible(f, values));
}

/** Clear values for fields that are hidden after a controlling answer changes */
export function pruneHiddenSurveyValues(values: InterestSurveyValues): InterestSurveyValues {
  const next = { ...values };
  for (const field of WORKSHOP_INTEREST_SURVEY) {
    if (!isFieldVisible(field, next)) {
      next[field.id] = '';
    }
  }
  return next;
}

export function validateInterestSurvey(values: InterestSurveyValues): string | null {
  for (const field of visibleSurveyFields(values)) {
    const v = (values[field.id] || '').trim();
    if (!v) {
      return `Please answer: ${field.label.replace(/\?$/, '')}.`;
    }
  }
  return null;
}

export function interestSurveyToPayload(values: InterestSurveyValues): {
  interest_responses: InterestSurveyValues;
} {
  const responses: InterestSurveyValues = {};
  for (const field of visibleSurveyFields(values)) {
    responses[field.id] = values[field.id].trim();
  }
  return { interest_responses: responses };
}

export function formatInterestDate(iso: string | null | undefined): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function labelForSurveyValue(field: SurveyField, value: string): string {
  const match = field.options.find((o) => o.value === value);
  return match?.label || value;
}

/** One-line summary for admin roster (e.g. "Some steps · Step 4 · Has sponsor: Yes"). */
export function summarizeInterestResponses(
  responses: Record<string, string> | null | undefined
): string {
  if (!responses || typeof responses !== 'object') return '';
  const parts: string[] = [];
  for (const field of WORKSHOP_INTEREST_SURVEY) {
    const raw = (responses[field.id] || '').trim();
    if (!raw) continue;
    if (!isFieldVisible(field, responses)) continue;
    parts.push(labelForSurveyValue(field, raw));
  }
  return parts.join(' · ');
}

/** Full questionnaire answers for expanded admin view. */
export function formatInterestResponsesDetail(
  responses: Record<string, string> | null | undefined
): { label: string; value: string }[] {
  if (!responses || typeof responses !== 'object') return [];
  const rows: { label: string; value: string }[] = [];
  for (const field of WORKSHOP_INTEREST_SURVEY) {
    const raw = (responses[field.id] || '').trim();
    if (!raw || !isFieldVisible(field, responses)) continue;
    rows.push({ label: field.label, value: labelForSurveyValue(field, raw) });
  }
  return rows;
}

export function collectMemberEmails(
  members: { email?: string; registration_state?: string }[],
  state: 'interest' | 'confirmed'
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of members) {
    if (m.registration_state !== state) continue;
    const email = (m.email || '').trim().toLowerCase();
    if (!email || !email.includes('@') || seen.has(email)) continue;
    seen.add(email);
    out.push(email);
  }
  return out.sort();
}
